/**
 * ============================================================================
 * BLACK FRIDAY PAGE COMPONENT
 * ============================================================================
 * 
 * TEMPORARY COMPONENT - Remove after Black Friday sale ends
 * 
 * To remove this feature:
 * 1. Delete this file (BlackFridayPage.js)
 * 2. Delete BlackFridayModal.js in this folder
 * 3. Remove import and <BlackFridayModal /> from Home.js
 * 4. Remove the /blackfriday route from App.js
 * 5. Delete the blackfriday.css styles from App.css (marked section)
 * 6. Delete blackfriday folder from /src/components/
 * 
 * ============================================================================
 */

import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';

function BlackFridayPage() {
  const [timeLeft, setTimeLeft] = useState({});
  
  // Sale ends Cyber Monday at midnight EST (Dec 1, 2025 11:59:59 PM EST)
  const saleEndDate = new Date('2025-12-02T04:59:59Z');

  // Gift card options - UPDATE LINKS HERE when ready
  // Gift card values: Pay 50% off, get full value gift card
  const giftCards = [
    { 
      value: 100,      // Gift card value
      salePrice: 50,   // 50% off price (what they pay)
      link: 'https://buy.stripe.com/8x2aEZ9Rfd7V8b6ae3eME09',
      popular: false
    },
    { 
      value: 150,      // Gift card value
      salePrice: 75,   // 50% off price (what they pay)
      link: 'https://buy.stripe.com/9B6fZj8NbaZN9facmbeME08',
      popular: true
    },
    { 
      value: 200,      // Gift card value
      salePrice: 100,  // 50% off price (what they pay)
      link: 'https://buy.stripe.com/3cIaEZ9Rf7NB3UQ99ZeME07',
      popular: false
    },
    { 
      value: 500,      // Gift card value
      salePrice: 250,  // 50% off price (what they pay)
      link: 'https://buy.stripe.com/9B65kFgfD8RF4YU3PFeME06',
      popular: false
    },
  ];

  // Calculate time remaining
  const calculateTimeLeft = () => {
    const difference = saleEndDate - new Date();
    
    if (difference > 0) {
      return {
        days: Math.floor(difference / (1000 * 60 * 60 * 24)),
        hours: Math.floor((difference / (1000 * 60 * 60)) % 24),
        minutes: Math.floor((difference / 1000 / 60) % 60),
        seconds: Math.floor((difference / 1000) % 60),
      };
    }
    return {};
  };

  // Update countdown every second
  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(calculateTimeLeft());
    }, 1000);

    setTimeLeft(calculateTimeLeft());
    return () => clearInterval(timer);
  }, []);

  return (
    <div className="bf-page min-h-screen bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* OG Meta Tags for Social Sharing */}
      <Helmet>
        <title>Black Friday 50% OFF Gift Cards | MyK9</title>
        <meta property="og:title" content="🎁 Black Friday 50% OFF Gift Cards!" />
        <meta property="og:description" content="Give the gift of happy pups! Get MyK9 gift cards at half price. Limited time only!" />
        <meta property="og:image" content="https://myk9.dog/og-blackfriday.png" />
        <meta property="og:url" content="https://myk9.dog/blackfriday" />
        <meta property="og:type" content="website" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="🎁 Black Friday 50% OFF Gift Cards!" />
        <meta name="twitter:description" content="Give the gift of happy pups! Get MyK9 gift cards at half price. Limited time only!" />
        <meta name="twitter:image" content="https://myk9.dog/og-blackfriday.png" />
      </Helmet>

      {/* Decorative Elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="bf-confetti"></div>
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#57a8cc]/20 rounded-full blur-3xl"></div>
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-[#57a8cc]/10 rounded-full blur-3xl"></div>
      </div>

      {/* Main Content */}
      <div className="relative z-10 container mx-auto px-4 py-8 md:py-12">

        {/* Header */}
        <div className="text-center mb-8 md:mb-12">
          <h1 className="text-4xl md:text-6xl font-Lobster text-[#57a8cc] mb-3">
            Black Friday
          </h1>
          <p className="text-3xl md:text-5xl font-black text-white mb-4">
            50% OFF Gift Cards!
          </p>
          <p className="text-slate-400 text-lg max-w-md mx-auto">
            Give the perfect gift this holiday season. Treat your favorite pup to daycare, grooming, and more!
          </p>
        </div>

        {/* Countdown Timer */}
        {Object.keys(timeLeft).length > 0 && (
          <div className="max-w-lg mx-auto mb-10 md:mb-14">
            <div className="bg-white/5 backdrop-blur-md rounded-2xl p-4 md:p-6 border border-white/10">
              <p className="text-center text-sm uppercase tracking-widest text-slate-400 mb-3">
                ⏰ Sale ends in
              </p>
              <div className="flex justify-center gap-2 md:gap-4">
                {timeLeft.days !== undefined && (
                  <div className="bf-timer-box bg-[#57a8cc]/20 rounded-xl p-3 md:p-4 min-w-[60px] md:min-w-[80px]">
                    <span className="text-2xl md:text-4xl font-bold text-white block">{timeLeft.days}</span>
                    <span className="text-xs text-slate-400">DAYS</span>
                  </div>
                )}
                <div className="bf-timer-box bg-[#57a8cc]/20 rounded-xl p-3 md:p-4 min-w-[60px] md:min-w-[80px]">
                  <span className="text-2xl md:text-4xl font-bold text-white block">{String(timeLeft.hours || 0).padStart(2, '0')}</span>
                  <span className="text-xs text-slate-400">HRS</span>
                </div>
                <div className="bf-timer-box bg-[#57a8cc]/20 rounded-xl p-3 md:p-4 min-w-[60px] md:min-w-[80px]">
                  <span className="text-2xl md:text-4xl font-bold text-white block">{String(timeLeft.minutes || 0).padStart(2, '0')}</span>
                  <span className="text-xs text-slate-400">MIN</span>
                </div>
                <div className="bf-timer-box bg-[#57a8cc]/20 rounded-xl p-3 md:p-4 min-w-[60px] md:min-w-[80px]">
                  <span className="text-2xl md:text-4xl font-bold text-white block">{String(timeLeft.seconds || 0).padStart(2, '0')}</span>
                  <span className="text-xs text-slate-400">SEC</span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Gift Cards Grid */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-6 max-w-5xl mx-auto mb-10">
          {giftCards.map((card, index) => (
            <div 
              key={card.value}
              className={`bf-gift-card relative group ${card.popular ? 'bf-popular' : ''}`}
            >
              {/* Popular Badge */}
              {card.popular && (
                <div className="absolute -top-3 left-1/2 transform -translate-x-1/2 z-10">
                  <span className="bg-[#57a8cc] text-white text-xs font-bold px-3 py-1 rounded-full whitespace-nowrap">
                    MOST POPULAR
                  </span>
                </div>
              )}

              <div className={`bf-card-inner bg-white/10 backdrop-blur-md rounded-2xl p-4 md:p-6 border transition-all duration-300 hover:scale-105 hover:bg-white/15 ${card.popular ? 'border-[#57a8cc] shadow-[0_0_30px_rgba(87,168,204,0.3)]' : 'border-white/10'}`}>
                
                {/* Gift Box Icon */}
                <div className="bf-gift-box mx-auto mb-4 w-20 h-20 md:w-24 md:h-24 relative">
                  {/* Box */}
                  <div className="absolute inset-0 bg-gradient-to-br from-[#57a8cc] to-[#4a95b6] rounded-lg transform rotate-0 group-hover:rotate-3 transition-transform">
                    {/* Ribbon Vertical */}
                    <div className="absolute left-1/2 transform -translate-x-1/2 w-3 h-full bg-red-400"></div>
                    {/* Ribbon Horizontal */}
                    <div className="absolute top-1/2 transform -translate-y-1/2 w-full h-3 bg-red-400"></div>
                  </div>
                  {/* Bow */}
                  <div className="absolute -top-3 left-1/2 transform -translate-x-1/2">
                    <div className="relative">
                      <div className="w-4 h-4 bg-red-400 rounded-full"></div>
                      <div className="absolute -left-3 top-0 w-4 h-3 bg-red-400 rounded-full transform -rotate-45"></div>
                      <div className="absolute -right-3 top-0 w-4 h-3 bg-red-400 rounded-full transform rotate-45"></div>
                    </div>
                  </div>
                </div>

                {/* Value */}
                <div className="text-center mb-4">
                  <p className="text-slate-400 text-sm line-through mb-1">${card.value} value</p>
                  <p className="text-4xl md:text-5xl font-black text-white">${card.salePrice}</p>
                </div>

                {/* Buy Button */}
                <a 
                  href={card.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bf-buy-btn block w-full bg-gradient-to-r from-[#57a8cc] to-[#4a95b6] hover:from-[#4a95b6] hover:to-[#3d8aa8] text-white font-bold py-3 px-4 rounded-xl text-center transition-all transform hover:scale-[1.02] active:scale-[0.98] shadow-lg no-underline"
                >
                  Buy Now
                </a>
              </div>
            </div>
          ))}
        </div>

        {/* Fine Print */}
        <p className="text-center text-xs text-slate-500 max-w-2xl mx-auto mb-8">
          *One gift card per client. Cannot be combined with any other offers or discounts.
        </p>

        {/* Trust Badges */}
        <div className="text-center mb-8">
          <div className="flex flex-wrap justify-center gap-4 md:gap-8 text-slate-400 text-sm">
            <span className="flex items-center gap-2">
              <span className="text-green-400">✓</span> Instant Delivery
            </span>
            <span className="flex items-center gap-2">
              <span className="text-green-400">✓</span> Never Expires
            </span>
            <span className="flex items-center gap-2">
              <span className="text-green-400">✓</span> Perfect Gift
            </span>
          </div>
        </div>

        {/* Availability Notice */}
        <div className="text-center">
          <p className="text-red-400 text-sm font-medium animate-pulse">
            ⚡ Limited availability - Only 50 gift cards at this price!
          </p>
        </div>

        </div>
    </div>
  );
}

export default BlackFridayPage;

